import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Type } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';

import { environment } from '@d-environments/environment';

import {
  BaseResponseModel,
  BaseSearchRequest,
  BaseSearchResponseModel,
} from '@d-models';

import { ModalService } from './modal.service';
import { ROW_PER_PAGE_DEFAULT } from '@d-constants';

@Injectable()
export class HttpService {
  BASE_URL = environment.API_URL;

  constructor(
    private httpClient: HttpClient,
    private modalService: ModalService,
  ) {}

  get<T>(
    url: string,
    modelAdapter?: Type<any>,
    options?: any,
    hideLoading = false,
  ): Observable<BaseResponseModel<T>> {
    const endpoint = `${this.BASE_URL}/${url}`;

    if (!hideLoading) this.modalService.showLoading();

    let _self = this.httpClient.get(endpoint, options) as any;

    return _self.pipe(
      tap<any>(() => {
        if (!hideLoading) this.modalService.hideLoading();
      }),
      map((res: any) => ({
        ...res,
        data:
          modelAdapter &&
          (Array.isArray(res.data)
            ? this.arrayModelAdapter(res.data, modelAdapter)
            : this.modelAdapter(res.data, modelAdapter)),
      })),
      catchError((e) => this.handleError(e)),
    );
  }

  post<T>(
    url: string,
    body: any,
    options?: any,
    hideLoading = false,
    modelAdapter?: Type<any>,
  ): Observable<any> {
    const endpoint = `${this.BASE_URL}/${url}`;

    if (!hideLoading) this.modalService.showLoading();
    return this.httpClient
      .post(endpoint, body, this.buildOptions(options))
      .pipe(
        tap<any>(() => {
          if (!hideLoading) this.modalService.hideLoading();
        }),
        map((res: BaseResponseModel<T>) => ({
          ...res,
          data: modelAdapter
            ? Array.isArray(res.data)
              ? this.arrayModelAdapter(res.data, modelAdapter)
              : this.modelAdapter(res.data, modelAdapter)
            : res.data,
        })),
        catchError((e) => this.handleError(e)),
      );
  }

  put<T>(
    url: string,
    body: any,
    options?: any,
    hideLoading = false,
  ): Observable<Object> {
    const endpoint = `${this.BASE_URL}/${url}`;

    if (!hideLoading) this.modalService.showLoading();

    return this.httpClient.put(endpoint, body, this.buildOptions(options)).pipe(
      tap<any>(() => this.modalService.hideLoading()),
      map((res: BaseResponseModel<T>) => ({
        ...res,
        data: res.data,
      })),
      catchError((e) => this.handleError(e)),
    );
  }

  delete(url: string, options?: any, hideLoading = false): Observable<Object> {
    const endpoint = `${this.BASE_URL}/${url}`;

    if (!hideLoading) this.modalService.showLoading();

    return this.httpClient.delete(endpoint, this.buildOptions(options)).pipe(
      tap<any>(() => this.modalService.hideLoading()),
      catchError((e) => this.handleError(e)),
    );
  }

  postSearch<T>(
    url: string,
    body: any,
    modelAdapter: Type<any>,
    options?: any,
    hideLoading = false,
  ): Observable<BaseSearchResponseModel<T>> {
    const endpoint = `${this.BASE_URL}/${url}`;

    if (!hideLoading) this.modalService.showLoading();

    return this.httpClient
      .post(endpoint, body, this.buildOptions(options))
      .pipe(
        tap<any>((res) => this.modalService.hideLoading()),
        map<BaseSearchResponseModel<T>, any>((res) => ({
          ...res,
          data: {
            ...res.data,
            data: this.arrayModelAdapter(res.data.data, modelAdapter),
          },
        })),
        catchError((e) => this.handleError(e)),
      );
  }

  getAll<T>(
    url: string,
    modelAdapter: Type<any>,
    options?: any,
    hideLoading = false,
  ): Observable<T[]> {
    const endpoint = `${this.BASE_URL}/${url}`;

    if (!hideLoading) this.modalService.showLoading();

    return this.httpClient.get(endpoint, this.buildOptions(options)).pipe(
      tap<any>((res) => this.modalService.hideLoading()),
      map<any, any>((res) => this.arrayModelAdapter(res.data, modelAdapter)),
      catchError((e) => this.handleError(e)),
    );
  }

  loadAll<T>(
    url: string,
    modelAdapter: Type<any>,
    options?: any,
    hideLoading = false,
  ): Observable<T[]> {
    const endpoint = `${this.BASE_URL}/${url}`;

    if (!hideLoading) this.modalService.showLoading();

    return this.httpClient.get(endpoint, this.buildOptions(options)).pipe(
      tap<any>((res) => this.modalService.hideLoading()),
      map<BaseSearchResponseModel<T>, any>((res) => ({
        ...res,
        data: {
          ...res.data,
          data: this.arrayModelAdapter(res.data.data, modelAdapter),
        },
      })),
      catchError((e) => this.handleError(e)),
    );
  }

  buildSearchRequest(event?: BaseSearchRequest, defaultFilterBy?: string) {
    return {
      pageIndex: event?.pageIndex || 1,
      pageSize: event?.rowPerPage || ROW_PER_PAGE_DEFAULT,
      sortBy:
        event?.sortBy?.map((i) => ({
          ...i,
          fieldName: i.fieldName.charAt(0).toUpperCase() + i.fieldName.slice(1),
        })) || null,
      searchGlobal: event?.searchGlobal || {
        filterBy: defaultFilterBy,
        searchBy: '',
      },
      advancedFilter: event?.advancedFilter || null,
      // programOwnerId: event?.programOwnerId || null,
      // productId: event?.productId || null,
      statuses: event?.statuses || null,
      // approvalFlowStatuses: event?.approvalFlowStatuses,
      // historyImportId: event?.historyImportId || null,
      // onlyShowLatestRevision: event?.onlyShowLatestRevision,
    };
  }

  buildSearchMerchantRequest(
    event?: BaseSearchRequest,
    defaultFilterBy?: string,
  ) {
    return {
      pageIndex: event?.pageIndex || 1,
      pageSize: event?.rowPerPage || ROW_PER_PAGE_DEFAULT,
      sortBy:
        event?.sortBy?.map((i) => ({
          ...i,
          fieldName: i.fieldName.charAt(0).toUpperCase() + i.fieldName.slice(1),
        })) || null,
      searchGlobal: event?.searchGlobal || {
        filterBy: defaultFilterBy,
        searchBy: '',
      },
      programmeTypeIds: event?.programmeTypeIds || null,
    };
  }

  buildSearchRequestWithoutDropdown(event?: BaseSearchRequest) {
    return {
      pageIndex: event?.pageIndex || 1,
      pageSize: event?.rowPerPage || ROW_PER_PAGE_DEFAULT,
      sortBy:
        event?.sortBy?.map((i) => ({
          ...i,
          fieldName: i.fieldName.charAt(0).toUpperCase() + i.fieldName.slice(1),
        })) || null,
      searchBy: event?.searchGlobal?.searchBy || '',
      advancedFilter: event?.advancedFilter || null,
      level: event?.level || 1,
    };
  }

  arrayModelAdapter(value: any[], clazz: Type<any>) {
    return value.map((i) => new clazz(i));
  }

  modelAdapter(value: any, clazz: Type<any>) {
    return new clazz(value);
  }

  private buildOptions(options: any) {
    return {
      ...options,
    };
  }

  private handleError(e: any) {
    this.modalService.hideLoading();
    console.error('This error from http: ', e);
    return throwError(() => new HttpErrorResponse(e));
  }
}
